/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMonetateDecisionFlag, getKiboDecisionKey } from '@/features/kibo/api/getKiboDecision';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type { MonetateData, MonetateFlagName } from '@/features/kibo';

const selectFlagWithTrueDefault = (queryClient: QueryClient | undefined, flag: string): boolean => {
  if (!queryClient) {
    return true;
  }
  const kiboDecisionData = queryClient.getQueryData<MonetateData>(getKiboDecisionKey());

  // For ACES-related flags, distinguish between missing flags and explicit false values
  const flagsToDefaultTrue = [
    'ACES_ENABLED',
    'TRANSITION_PHASE',
    'ACES_PARTS_PRO_FLAG',
    'alternativeYMMEContentBlock',
  ];

  if (flagsToDefaultTrue.includes(flag)) {
    if (!kiboDecisionData || !kiboDecisionData.flags || !(flag in kiboDecisionData.flags)) {
      // Flag is missing from response (paused/no experiment) - default to true
      return true;
    }
    // Flag exists in response - use actual value (could be true or false)
    return Boolean(kiboDecisionData.flags[flag].value);
  }

  // For other flags, use standard behavior
  if (!kiboDecisionData || !kiboDecisionData.flags || !(flag in kiboDecisionData.flags)) {
    return false;
  }
  return Boolean(kiboDecisionData.flags[flag].value);
};

const useMonetateDecisionFlagWithTrueDefault = (flag: MonetateFlagName): boolean => {
  const result = useMonetateDecisionFlag(flag);
  const queryClient = useQueryClient();

  // For the flagsToDefaultTrue flags ('ACES_ENABLED', 'TRANSITION_PHASE',
  // 'ACES_PARTS_PRO_FLAG', 'alternativeYMMEContentBlock'), we need to distinguish
  // between explicit false (active experiment) and missing flag (paused/no experiment)
  const flagsToDefaultTrue: MonetateFlagName[] = [
    'ACES_ENABLED',
    'TRANSITION_PHASE',
    'ACES_PARTS_PRO_FLAG',
    'alternativeYMMEContentBlock',
  ];
  if (flagsToDefaultTrue.includes(flag)) {
    // Check if flag actually exists in the Monetate response to distinguish
    // between explicit false and missing flag
    const kiboDecisionData = queryClient.getQueryData<MonetateData>(getKiboDecisionKey());
    const flagExists = kiboDecisionData?.flags && flag in kiboDecisionData.flags;

    if (!flagExists) {
      // Flag is missing from response (paused/no experiment) - default to true
      return true;
    }
    // Flag exists in response - use actual value (could be true or false)
    return Boolean(result);
  }
  return Boolean(result);
};

export type AcesFlags = {
  acesEnabled: boolean;
  acesTransitionPhase: boolean;
  partsProFlag: boolean;
  alternativeYMMEContentBlock: boolean;
};

export const useAcesFlags = (): AcesFlags => {
  const acesEnabled = useMonetateDecisionFlagWithTrueDefault('ACES_ENABLED');
  const acesTransitionPhase = useMonetateDecisionFlagWithTrueDefault('TRANSITION_PHASE');
  const partsProFlag = useMonetateDecisionFlagWithTrueDefault('ACES_PARTS_PRO_FLAG');
  const alternativeYMMEContentBlock = useMonetateDecisionFlagWithTrueDefault(
    'alternativeYMMEContentBlock',
  );

  return {
    acesEnabled,
    acesTransitionPhase,
    partsProFlag,
    alternativeYMMEContentBlock,
  };
};

export const getAcesFlag = (queryClient: QueryClient | undefined): AcesFlags => {
  const acesEnabled = selectFlagWithTrueDefault(queryClient, 'ACES_ENABLED');
  const acesTransitionPhase = selectFlagWithTrueDefault(queryClient, 'TRANSITION_PHASE');
  const partsProFlag = selectFlagWithTrueDefault(queryClient, 'ACES_PARTS_PRO_FLAG');
  const alternativeYMMEContentBlock = selectFlagWithTrueDefault(
    queryClient,
    'alternativeYMMEContentBlock',
  );

  return {
    acesEnabled,
    acesTransitionPhase,
    partsProFlag,
    alternativeYMMEContentBlock,
  };
};
